import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Button,
  Select,
  DatePicker,
  Input,
  Row,
  Col,
  Space,
  Modal,
  Form,
  message,
  Popconfirm,
} from "antd";
import { SearchOutlined, ArrowRightOutlined, StopOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { selectAuthInfo } from "../../Auth/authSelectors";
import { accessChecker } from "../../../lib/functions";
import { fetchDeliveryJobs, advanceJob, cancelDeliveryJob } from "../deliveryThunks";
import {
  selectDeliveryJobs,
  selectDeliveryJobsLoading,
} from "../deliverySelectors";

const { RangePicker } = DatePicker;
const { Option } = Select;

const STATUS_LABELS = {
  picking: "Picking",
  picking_verification: "Picking Verification",
  tatmeen_verification: "Tatmeen Verification",
  packing: "Packing",
  on_transit: "On Transit",
  delivered: "Delivered",
  completed: "Completed",
  cancelled: "Cancelled",
};

const STATUS_COLORS = {
  picking: "blue",
  picking_verification: "purple",
  tatmeen_verification: "cyan",
  packing: "blue",
  on_transit: "geekblue",
  delivered: "green",
  completed: "success",
  cancelled: "red",
};

const DeliveryJobs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authInfo = useSelector(selectAuthInfo);
  const jobs = useSelector(selectDeliveryJobs);
  const jobsLoading = useSelector(selectDeliveryJobsLoading);

  const hasWriteAccess = accessChecker("delivery_jobs_manage");

  const [filters, setFilters] = useState({
    status: null,
    delivery_type: null,
    date_range: null,
    search: "",
  });

  const [advanceModal, setAdvanceModal] = useState({
    open: false,
    jobId: null,
  });
  const [advanceForm] = Form.useForm();
  const [advanceLoading, setAdvanceLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(null); // stores jobId being cancelled

  const loadJobs = (overrides = {}) => {
    const merged = { ...filters, ...overrides };
    const payload = {};
    if (merged.status) payload.status = merged.status;
    if (merged.delivery_type) payload.delivery_type = merged.delivery_type;
    if (merged.date_range && merged.date_range[0] && merged.date_range[1]) {
      payload.from_date = merged.date_range[0].format("YYYY-MM-DD");
      payload.to_date = merged.date_range[1].format("YYYY-MM-DD");
    }
    if (merged.search) payload.search = merged.search;
    dispatch(fetchDeliveryJobs(payload));
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const handleFilterChange = (key, value) => {
    const updated = { ...filters, [key]: value };
    setFilters(updated);
    loadJobs(updated);
  };

  const handleAdvanceSubmit = async () => {
    try {
      const values = await advanceForm.validateFields();
      setAdvanceLoading(true);
      await dispatch(
        advanceJob({ id: advanceModal.jobId, notes: values.notes || "" })
      ).unwrap();
      message.success("Job advanced successfully");
      setAdvanceModal({ open: false, jobId: null });
      advanceForm.resetFields();
      loadJobs();
    } catch (err) {
      message.error(
        err?.message || err?.error || "Failed to advance job"
      );
    } finally {
      setAdvanceLoading(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      setCancelLoading(id);
      await dispatch(cancelDeliveryJob({ id, notes: "" })).unwrap();
      message.success("Job cancelled");
      loadJobs();
    } catch (err) {
      message.error(err?.message || err?.error || "Failed to cancel job");
    } finally {
      setCancelLoading(null);
    }
  };

  const columns = [
    {
      title: "Voucher No",
      dataIndex: "sale_voucher_no",
      key: "sale_voucher_no",
      render: (val, record) => {
        const label = val || `#${record.id}`;
        return (
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/delivery/jobs/${record.id}`)}>
            {label}
          </Button>
        );
      },
    },
    {
      title: "Customer",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (val, record) => val || record.customer || "-",
    },
    {
      title: "Delivery Type",
      dataIndex: "delivery_type",
      key: "delivery_type",
      render: (val) =>
        val ? <Tag color="blue">{val}</Tag> : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) =>
        val ? (
          <Tag color={STATUS_COLORS[val] || "default"}>
            {STATUS_LABELS[val] || val}
          </Tag>
        ) : "-",
    },
    {
      title: "Assigned To",
      key: "assigned_user",
      render: (_, record) =>
        record.picker_name ||
        record.delivery_agent_name ||
        record.packer_name ||
        record.verifier_name ||
        "-",
    },
    {
      title: "Delivery Date",
      dataIndex: "delivery_date",
      key: "delivery_date",
      render: (val) =>
        val ? dayjs(val).format("DD MMM YYYY") : "-",
    },
    {
      title: "Created Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (val) =>
        val ? dayjs(val).format("DD MMM YYYY") : "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            type="primary"
            onClick={() => navigate(`/delivery/jobs/${record.id}`)}
          >
            View
          </Button>
          {hasWriteAccess &&
            record.status !== "completed" &&
            record.status !== "cancelled" && (
              <Button
                size="small"
                icon={<ArrowRightOutlined />}
                onClick={() =>
                  setAdvanceModal({ open: true, jobId: record.id })
                }
              >
                Advance
              </Button>
            )}
          {hasWriteAccess &&
            record.status !== "completed" &&
            record.status !== "cancelled" && (
              <Popconfirm
                title="Cancel this job?"
                description="This action cannot be undone."
                onConfirm={() => handleCancel(record.id)}
                okText="Cancel Job"
                okButtonProps={{ danger: true }}
                cancelText="Keep"
              >
                <Button
                  size="small"
                  danger
                  icon={<StopOutlined />}
                  loading={cancelLoading === record.id}
                >
                  Cancel
                </Button>
              </Popconfirm>
            )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Filters */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Select
            placeholder="Filter by Status"
            allowClear
            style={{ width: "100%" }}
            onChange={(val) => handleFilterChange("status", val)}
            value={filters.status}
          >
            {Object.entries(STATUS_LABELS).map(([key, label]) => (
              <Option key={key} value={key}>
                {label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={5}>
          <Input
            placeholder="Delivery Type"
            allowClear
            value={filters.delivery_type}
            onChange={(e) =>
              handleFilterChange("delivery_type", e.target.value || null)
            }
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <RangePicker
            style={{ width: "100%" }}
            format="DD MMM YYYY"
            onChange={(dates) => handleFilterChange("date_range", dates)}
          />
        </Col>
        <Col xs={24} sm={12} md={5}>
          <Input
            placeholder="Search..."
            prefix={<SearchOutlined />}
            allowClear
            value={filters.search}
            onChange={(e) =>
              handleFilterChange("search", e.target.value)
            }
          />
        </Col>
      </Row>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={jobs}
        loading={jobsLoading}
        rowKey={(r) => r.id}
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: true }}
      />

      {/* Advance Modal */}
      <Modal
        title="Advance Job"
        open={advanceModal.open}
        onCancel={() => {
          setAdvanceModal({ open: false, jobId: null });
          advanceForm.resetFields();
        }}
        onOk={handleAdvanceSubmit}
        confirmLoading={advanceLoading}
        okText="Advance"
      >
        <Form form={advanceForm} layout="vertical">
          <Form.Item name="notes" label="Notes (optional)">
            <Input.TextArea rows={3} placeholder="Add notes..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DeliveryJobs;
